import { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { SubscriptionModel } from '../interfaces';
import { SubscriptionProduct, subscriptionProductNames } from '../interfaces.enums';
import { CREATE_SUBSCRIPTION, GET_MACONOMY_ITEM, GET_SUBSCRIPTIONS, UPDATE_SUBSCRIPTION } from './subscriptions.graphql';
import { suggestNumber } from './suggestNumber';
import { SalesTextField, salesTextError } from './salesText';

export interface SubscriptionUsage {
  subscriptionId: number;
  licenses: number;
  users: number;
}

interface Props {
  subscription?: SubscriptionModel;
  subscriptions: SubscriptionModel[];
  usage?: SubscriptionUsage;
  onClose(): void;
}

// The BKS column sizes, also enforced by the API.
const MaxNameLength = 120;
const MaxNumberLength = 30;
export function SubscriptionDialog({ subscription, subscriptions, usage, onClose }: Props) {
  const isNew = !subscription;

  const [name, setName] = useState(subscription?.name ?? '');
  const [typedNumber, setTypedNumber] = useState<string | null>(isNew ? null : (subscription.number ?? ''));
  const [isSellable, setIsSellable] = useState(subscription?.isSellable ?? true);
  const [isRecommended, setIsRecommended] = useState(subscription?.isRecommended ?? false);
  const [singleUser, setSingleUser] = useState(0);
  const [extraUser, setExtraUser] = useState(0);
  const [product, setProduct] = useState<SubscriptionProduct | ''>('');
  const [upgradesTo, setUpgradesTo] = useState<number | ''>(subscription?.upgradesTo ?? '');
  const [salesText, setSalesText] = useState(subscription?.salesText ?? '');
  const [error, setError] = useState<string | null>(null);

  // GetProductChanges by name: the change log's variables depend on the selected product, so refetch the query that is active.
  const [create, { loading: creating }] = useMutation(CREATE_SUBSCRIPTION, {
    refetchQueries: [{ query: GET_SUBSCRIPTIONS }, 'GetProductChanges'],
    awaitRefetchQueries: true,
  });
  // Recommending one takes the mark from another, so the list is fetched again.
  const [update, { loading: updating }] = useMutation(UPDATE_SUBSCRIPTION, { refetchQueries: ['GetProductChanges', 'GetSubscriptions'] });
  const loading = creating || updating;

  // Maconomy lines are matched by number, so neither it nor the parent moves once licences have the subscription.
  const inUse = !!usage && (usage.licenses > 0 || usage.users > 0);
  const inUseText = inUse ? `Kan ikke endres: brukt av ${usage.licenses} lisenser og ${usage.users} brukere.` : undefined;
  // Parts predate this dialog: the parent is only shown, never chosen, and passed back unchanged.
  const parentId = subscription?.parentId ?? null;
  const parent = subscriptions.find((x) => x.bksId === parentId);
  const number = typedNumber ?? suggestNumber(name, subscriptions);
  const numberChanged = number.trim().toUpperCase() !== (subscription?.number ?? '').toUpperCase();

  // The product decides the Maconomy item group, and so the revenue account. It is asked for only when neither the
  // parent, the subscription nor an existing Maconomy item gives one.
  const knownProduct = parent?.product ?? subscription?.product ?? null;
  const maconomy = useMaconomyItem(number.trim(), !inUse && numberChanged, knownProduct);
  const needsProduct = !knownProduct && (maconomy.status === 'missing' || maconomy.status === 'unknown' || maconomy.status === 'checking');
  // A top-level subscription made before products can be given one, or left in neither.
  const canChooseProduct = needsProduct || (!isNew && !knownProduct);
  // The API also refuses an upgrade target that leads back here.
  const upgradeProduct = knownProduct ?? (product || null);
  const upgradeOptions = subscriptions.filter(
    (x) => x.bksId !== subscription?.bksId && x.isSellable && !!upgradeProduct && x.product === upgradeProduct,
  );
  const recommendedNow = subscriptions.find(
    (x) => x.bksId !== subscription?.bksId && x.isRecommended && !!upgradeProduct && x.product === upgradeProduct,
  );
  // A target saved before it was taken off sale, or moved, is still shown, so the select does not look empty.
  const invalidTarget = subscriptions.find((x) => x.bksId === subscription?.upgradesTo && !upgradeOptions.includes(x));

  // The trial is never invoiced and has no number.
  const numberRequired = isNew || isSellable;
  const valid =
    name.trim() &&
    (!numberRequired || number.trim()) &&
    (!needsProduct || product) &&
    maconomy.status !== 'foreign' &&
    singleUser >= 0 &&
    extraUser >= 0 &&
    salesTextError(salesText) === null;

  const save = async () => {
    setError(null);
    try {
      if (isNew) {
        await create({
          variables: {
            input: { name: name.trim(), number: number.trim(), singleUser, extraUser, product: needsProduct && product ? product : null },
          },
        });
      } else {
        await update({
          variables: {
            input: {
              subscriptionId: subscription.bksId,
              name: name.trim(),
              number: number.trim() || null,
              parentId,
              isSellable,
              product: canChooseProduct && product ? product : null,
              upgradesTo: upgradesTo === '' ? null : upgradesTo,
              salesText: salesText.trim() || null,
              isRecommended: isSellable && isRecommended,
            },
          },
        });
      }
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Kunne ikke lagre abonnementet.');
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>{isNew ? 'Nytt abonnement' : `Rediger ${subscription.name}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="Navn"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
            slotProps={{ htmlInput: { maxLength: MaxNameLength } }}
          />
          <TextField
            label="Maconomy-nummer"
            value={number}
            onChange={(e) => setTypedNumber(e.target.value)}
            helperText={inUseText ?? maconomy.text ?? (isNew ? 'Foreslått ut fra navnet. Kan endres.' : undefined)}
            error={maconomy.status === 'foreign'}
            required={numberRequired}
            disabled={inUse}
            slotProps={{ htmlInput: { maxLength: MaxNumberLength } }}
          />
          {canChooseProduct && (
            <TextField
              select
              label="Produkt"
              value={product}
              onChange={(e) => setProduct(e.target.value as SubscriptionProduct)}
              helperText={
                needsProduct
                  ? 'Bestemmer varegruppen, og dermed inntektskontoen, når artikkelen opprettes i Maconomy.'
                  : 'Hvilke rapporter, bruksgrenser og e-postlister som teller abonnementet. Kan stå tomt for et abonnement i ingen av dem.'
              }
              required={needsProduct}
            >
              <MenuItem value={SubscriptionProduct.BFS}>Byggforskserien</MenuItem>
              <MenuItem value={SubscriptionProduct.BVN}>Våtromsnormen</MenuItem>
            </TextField>
          )}
          {knownProduct && !isNew && (
            <TextField
              label="Produkt"
              value={subscriptionProductNames[knownProduct]}
              helperText={parent ? `Samme som ${parent.name}.` : undefined}
              disabled
            />
          )}
          {parent && <TextField label="Del av" value={parent.name} disabled />}
          {!isNew && (
            <SalesTextField
              value={salesText}
              onChange={setSalesText}
              about="Listes på abonnementets kort i portalen der det selges, så si hva det gir og hva som skiller det fra et annet."
            />
          )}
          {!isNew && (
            <TextField
              select
              label="Kan oppgraderes til"
              value={upgradesTo}
              onChange={(e) => setUpgradesTo(e.target.value === '' ? '' : Number(e.target.value))}
              helperText={
                !upgradeProduct
                  ? 'Velg produkt først.'
                  : invalidTarget && upgradesTo === invalidTarget.bksId
                    ? `«${invalidTarget.name}» er ikke lenger til salgs i samme produkt. Velg et annet, eller Ingen.`
                    : isSellable
                      ? 'En lisens med dette abonnementet kan oppgraderes til det valgte, og en lisens med det valgte kan nedgraderes hit.'
                      : 'En lisens med dette abonnementet kan oppgraderes til det valgte.'
              }
              error={!!invalidTarget && upgradesTo === invalidTarget.bksId}
              disabled={!upgradeProduct}
            >
              <MenuItem value="">Ingen</MenuItem>
              {invalidTarget && <MenuItem value={invalidTarget.bksId}>{invalidTarget.name} (ikke gyldig lenger)</MenuItem>}
              {upgradeOptions.map((x) => (
                <MenuItem key={x.bksId} value={x.bksId}>
                  {x.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {isNew ? (
            <Stack direction="row" spacing={2}>
              <TextField
                label="1 bruker"
                type="number"
                value={singleUser}
                onChange={(e) => setSingleUser(Number(e.target.value))}
                required
                fullWidth
                slotProps={{ input: { startAdornment: <InputAdornment position="start">kr</InputAdornment> }, htmlInput: { min: 0 } }}
              />
              <TextField
                label="Ekstra brukere"
                helperText="Pris per ekstra bruker"
                type="number"
                value={extraUser}
                onChange={(e) => setExtraUser(Number(e.target.value))}
                required
                fullWidth
                slotProps={{ input: { startAdornment: <InputAdornment position="start">kr</InputAdornment> }, htmlInput: { min: 0 } }}
              />
            </Stack>
          ) : (
            <FormControlLabel
              control={<Switch checked={isSellable} onChange={(e) => setIsSellable(e.target.checked)} disabled={!subscription.price} />}
              label={subscription.price ? 'Til salgs' : 'Til salgs (krever en pris)'}
            />
          )}
          {!isNew && (
            <Stack spacing={0.25}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isSellable && isRecommended}
                    onChange={(e) => setIsRecommended(e.target.checked)}
                    disabled={!isSellable || !upgradeProduct}
                  />
                }
                label="Anbefalt i portalen"
              />
              <Typography variant="caption" color="text.secondary">
                {!isSellable
                  ? 'Bare et abonnement til salgs kan anbefales.'
                  : isRecommended && recommendedNow
                    ? `Får merket «Anbefalt» på kjøpssiden i stedet for «${recommendedNow.name}».`
                    : 'Får merket «Anbefalt» på kjøpssiden. Bare ett abonnement per produkt kan være anbefalt.'}
              </Typography>
            </Stack>
          )}
          {isNew && <Alert severity="info">Abonnementet gir hele produktet, med historiske anvisninger. Det kan endres under Innhold etterpå.</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>
          Avbryt
        </Button>
        <Button variant="contained" disabled={!valid} loading={loading} onClick={save}>
          {isNew ? 'Opprett' : 'Lagre'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type MaconomyStatus = 'checking' | 'unknown' | 'missing' | 'exists' | 'blocked' | 'foreign';

function useMaconomyItem(number: string, enabled: boolean, product: SubscriptionProduct | null): { status?: MaconomyStatus; text?: string } {
  const [check, { data, loading, error }] = useLazyQuery(GET_MACONOMY_ITEM, { fetchPolicy: 'network-only' });
  const [checked, setChecked] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !number) return;
    const timer = setTimeout(() => {
      setChecked(number);
      void check({ variables: { number } });
    }, 400);
    return () => clearTimeout(timer);
  }, [number, enabled, check]);

  if (!enabled || !number) return {};
  if (checked !== number || loading) return { status: 'checking', text: 'Sjekker Maconomy …' };
  if (error) return { status: 'unknown', text: 'Fikk ikke sjekket Maconomy. Det sjekkes igjen når du lagrer.' };
  const item = data?.item;
  if (!item)
    return {
      status: 'missing',
      text: product
        ? `Finnes ikke i Maconomy, og opprettes i ${subscriptionProductNames[product]} når du lagrer.`
        : 'Finnes ikke i Maconomy, og opprettes når du lagrer.',
    };
  if (!item.product) return { status: 'foreign', text: `«${item.title}» i Maconomy er ikke et abonnement. Velg et annet nummer.` };
  if (product && item.product !== product)
    return {
      status: 'foreign',
      text: `«${item.title}» i Maconomy hører til ${subscriptionProductNames[item.product]}, men abonnementet hører til ${subscriptionProductNames[product]}. Velg et annet nummer.`,
    };
  const existing = `«${item.title}» i ${subscriptionProductNames[item.product]}`;
  if (item.blocked)
    return { status: 'blocked', text: `Finnes i Maconomy, men er sperret: ${existing}. Den åpnes igjen med det nye navnet når du lagrer.` };
  return { status: 'exists', text: `Finnes i Maconomy: ${existing}. Artikkelen brukes som den er.` };
}
